import { GET_PARAMS, GET_POPUP_TYPES } from "constants/popupConfig";
import { useEffect } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import {
  normalizeNotifications,
  useNotificationsStore,
} from "stores/notifications";

export const useNotificationPopups = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    canShowNext,
    currentNotification,
    notifications,
    removeNotification,
    setCurrentNotification,
  } = useNotificationsStore((state) => ({
    canShowNext: state.canShowNext,
    currentNotification: state.currentNotification,
    notifications: state.notifications,
    removeNotification: state.removeNotification,
    setCurrentNotification: state.setCurrentNotification,
  }));

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    const isGamePage =
      (location.pathname.includes("/game") &&
        !location.pathname.includes("/profile/games")) ||
      (location.pathname.includes("/slots") &&
        location.pathname.split("/slots/")[1]?.length > 0);

    const isDepositPage =
      location.pathname.includes(GET_POPUP_TYPES.popup.deposit) ||
      location.search.includes(GET_POPUP_TYPES.popup.deposit) ||
      location.search.includes(GET_POPUP_TYPES.popup.withdraw);

    const preparedNotifications = normalizeNotifications(notifications);

    if (
      preparedNotifications.length > 0 &&
      !isGamePage &&
      !isDepositPage &&
      !currentNotification &&
      !preparedNotifications[0]?.disabled &&
      canShowNext
    ) {
      if (preparedNotifications[0].kind === "SystemFirstDeposit") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-welcome", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "SystemSuccessfulDeposit") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-first-deposit", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "SystemBalanceUtilization") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-utilization-balance", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "SystemFirstFailedDeposit") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-first-failed-deposit", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "SystemSecondFailedDeposit") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-second-failed-deposit", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "SystemRestFailedDeposits") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-third-failed-deposit", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }

      // Bonus preparedNotifications
      if (preparedNotifications[0].kind === "BonusCampaignAvailable") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-independence-day", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "BonusActivationTerm24") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-independence-counter", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`, // none
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "BonusAccruedReward") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-bonus-money", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "BonusWageringTerm24") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-wagering-counter", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`, // none
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "BonusTransferToMain") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-bonus-added", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "FreeSpinAvailable") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-freespin-available", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "FreeSpinAssigned") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-freespin-assigned", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "FreeSpinWinAccrued") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: "notification-freespin-accured", [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "BoosterAssigned") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.boosterAvailable, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "RankAssigned") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.rankUp, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "RankLevelAssigned") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.levelRankUp, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "UserProgressInactive") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.daysWithoutProgress, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "CashbackAccumulated") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.cashbackAssign, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "RollbackCountdownTimeExpired") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.levelDanger, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "RollbackTimeExpired") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.levelDanger, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      if (preparedNotifications[0].kind === "RollbackOccurred") {
        setCurrentNotification(preparedNotifications[0]);
        navigate({
          search: `?${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.rollback, [GET_PARAMS.NOTIFICATION_ID]: String(preparedNotifications[0]?.id) })}`,
        });
        removeNotification(preparedNotifications[0]?.id);
        return;
      }
      removeNotification(preparedNotifications[0]?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, currentNotification, location, canShowNext]);
};
